import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { createRelationship, fetchRelationships } from "../redux/actions/relationshipActions";
import { useNavigate } from "react-router-dom";
import { useDeduplicatedRelationships } from "../hooks/useDeduplicatedRelationships";
import {
  Autocomplete,
  Container,
  FormControl,
  Stack,
  FormLabel,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  Select,
  CircularProgress,
  Alert,
  Snackbar,
  MenuItem,
  Breadcrumbs,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { textFieldStyles } from "../styles/textFieldStyles";
import { TextFieldsTwoTone } from "@mui/icons-material";
import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import format from "date-fns/esm/format";
import parseISO from "date-fns/esm/parseISO";
import enGB from "date-fns/esm/locale/en-GB";

const getBreadcrumbs = (t) => [
  { label: t('pages:dashboard.title'), href: "/" },
  { label: t('common:relationships'), href: "/relationships" },
];
const tableStyles = windowsTableStyles();

const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

const MUIDatePicker = ({ value, onChange, sx, label, required }) => {
  const parsed = value ? parseISO(value) : null;

  return (
    <FormControl variant="outlined" size="small" sx={sx}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={enGB}
      >
        <DatePicker
          label={label}
          value={parsed}
          onChange={(newDate) => {
            if (newDate) {
              onChange(format(newDate, 'yyyy-MM-dd'));
            } else {
              onChange('');
            }
          }}
          inputFormat="dd/MM/yyyy"
          mask="__/__/____"
          required={required}
          slotProps={{
            textField: {
              size: 'small',
              required: required,
              sx: {
                // target the OutlinedInput root:
                '& .MuiOutlinedInput-root': {
                  height: 33.53,            // your desired total height
                  paddingRight: 2,       // remove extra right padding if any
                },
                // tweak the inner input padding if needed:
                '& .MuiOutlinedInput-input': {
                  padding: '8px 14px',
                },
                // shrink the calendar icon if it's too big:
                '& .MuiInputAdornment-root svg': {
                  fontSize: '1.2rem',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

const CreateRelationshipPage = () => {
  const { t } = useTranslation(['common', 'pages', 'forms', 'buttons', 'messages']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  // List of marital status options
  const maritalStatusOptions = [
    { value: "Single", label: t('forms:options.marital_status.single') },
    { value: "Married", label: t('forms:options.marital_status.married') },
    { value: "Divorced", label: t('forms:options.marital_status.divorced') },
    { value: "Widowed", label: t('forms:options.marital_status.widowed') }
  ];
  // List of sex options
  const sexOptions = [
    { value: "Male", label: t('forms:options.sex.male') },
    { value: "Female", label: t('forms:options.sex.female') },
    { value: "Unknown", label: t('forms:options.sex.unknown') }
  ];
  // List of European countries with translations
  const europeanCountries = [
    { value: "Netherlands", label: t('common:countries.Netherlands') },
    { value: "Belgium", label: t('common:countries.Belgium') },
    { value: "Germany", label: t('common:countries.Germany') },
    { value: "France", label: t('common:countries.France') },
    { value: "Italy", label: t('common:countries.Italy') },
    { value: "Spain", label: t('common:countries.Spain') },
    { value: "Portugal", label: t('common:countries.Portugal') },
    { value: "Sweden", label: t('common:countries.Sweden') },
    { value: "Norway", label: t('common:countries.Norway') },
    { value: "Denmark", label: t('common:countries.Denmark') },
    { value: "Finland", label: t('common:countries.Finland') },
    { value: "Poland", label: t('common:countries.Poland') },
    { value: "Austria", label: t('common:countries.Austria') },
    { value: "Switzerland", label: t('common:countries.Switzerland') },
    { value: "Greece", label: t('common:countries.Greece') },
    { value: "Ireland", label: t('common:countries.Ireland') },
    { value: "Czech Republic", label: t('common:countries.Czech Republic') },
    { value: "Hungary", label: t('common:countries.Hungary') },
    { value: "Romania", label: t('common:countries.Romania') },
    { value: "Bulgaria", label: t('common:countries.Bulgaria') },
    { value: "Croatia", label: t('common:countries.Croatia') },
    { value: "Slovakia", label: t('common:countries.Slovakia') },
    { value: "Slovenia", label: t('common:countries.Slovenia') },
    { value: "Estonia", label: t('common:countries.Estonia') },
    { value: "Latvia", label: t('common:countries.Latvia') },
    { value: "Lithuania", label: t('common:countries.Lithuania') },
    { value: "Luxembourg", label: t('common:countries.Luxembourg') },
    { value: "Malta", label: t('common:countries.Malta') },
    { value: "Cyprus", label: t('common:countries.Cyprus') },
  ];

  // Initial form state
  const initialFormState = {
    firstName: "",
    middleName: "",
    lastName: "",
    name: "",
    type: "Client",
    email: "",
    status: "",
    sector: "",
    businessName: "",
    website: "",
    kvk: "",
    bsn: "",
    sex: "",
    burgerlijkeStaat: "",
    contractType: "Individual",
    dateOfBirth: "",
    contacts: [{ name: "", email: "", phone: "", position: "" }],
    addresses: [
      { address: "", postcode: "", place: "", country: "Netherlands" },
    ],
    bankAccounts: [{ accountNumber: "", holder: "", bankAddress: "" }],
    linkedIdentities: [{ identity: "" }],
  };

  const [relationship, setRelationship] = useState(initialFormState);

  const [tabIndex, setTabIndex] = useState(0);

  // Function to reset form to initial state
  const resetForm = useCallback(() => {
    setRelationship(initialFormState);
    setTabIndex(0);
  }, []);
  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);
  
  // Fetch relationships on mount for duplicate checking
  useEffect(() => {
    dispatch(fetchRelationships());
  }, [dispatch]);
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const { loading, success, error } = useSelector(
    (state) => state.relationshipCreate
  );
  
  const { relationships: allRelationships } = useSelector((state) => state.relationshipList);
  
  // Use deduplicated relationships for duplicate checking
  const relationships = useDeduplicatedRelationships(allRelationships);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingRelationship: null,
    message: ""
  });

  // Get newly created relationship ID from state
  const { relationship: newRelationship } = useSelector((state) => state.relationshipCreate);
  
  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('pages:activity.relationship_created_successfully'),
        severity: "success",
      });

      // Check if we came from CreateFilePage
      const fromFilePage = localStorage.getItem('relationshipCreationStartTime') &&
                          sessionStorage.getItem('inPartyCreationFlow') === 'true';

      if (fromFilePage && newRelationship && newRelationship._id) {
        console.log('🎯 Relationship created from CreateFilePage flow:', {
          relationshipId: newRelationship._id,
          relationshipName: newRelationship.name || newRelationship.businessName,
          relationshipType: newRelationship.type
        });

        // Save the ID to be used when returning to CreateFilePage
        sessionStorage.setItem('lastCreatedRelationshipId', newRelationship._id);
        console.log('✅ Created relationship ID saved to sessionStorage:', newRelationship._id);

        // Set timestamp for debugging
        sessionStorage.setItem('relationshipCreationTimestamp', Date.now().toString());
      } else if (!localStorage.getItem('relationshipCreationStartTime')) {
        // Normal relationship creation flow - clear form, reset state, and navigate
        console.log('🔄 Normal relationship creation flow - clearing form and navigating');
        
        // Refresh relationships list to include the newly created relationship
        dispatch(fetchRelationships());
        
        // Clear the form fields
        resetForm();
        
        // Reset Redux state
        dispatch({ type: "CREATE_RELATIONSHIP_RESET" });
        
        // Navigate to relationships page after a short delay to show success message
        setTimeout(() => {
          navigate("/relationships");
        }, 1500);
      }
    } else if (error) {
      console.error('❌ Relationship creation failed:', error);
      
      // Check if error is due to duplicate
      if (error.includes('duplicate') || error.includes('already exists')) {
        setNotification({
          open: true,
          message: error,
          severity: "error",
        });
      } else {
        setNotification({
          open: true,
          message: error,
          severity: "error",
        });
      }
      dispatch({ type: "CREATE_RELATIONSHIP_RESET" });
    }
  }, [success, error, dispatch, newRelationship, t, navigate, resetForm]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const generateAvatar = (relationship) => {
    const colors = [
      "#FAD7A0",
      "#AED6F1",
      "#ABEBC6",
      "#F5B7B1",
      "#F9E79F",
      "#D7BDE2",
    ];
    const bgColor = colors[Math.floor(Math.random() * colors.length)];

    let initials;
    if (relationship.contractType === "Individual") {
      const f = relationship.firstName?.[0] ?? "";
      const l = relationship.lastName?.[0] ?? "";
      initials = `${f}${l}`;
    } else {
      initials = relationship.businessName?.[0] ?? "";
    }

    const avatarURL = `https://ui-avatars.com/api/?name=${initials}&background=${bgColor.replace(
      "#",
      ""
    )}&color=000&size=128`;

    return avatarURL;
  };

  // Helper function to normalize strings for duplicate checking
  const normalizeString = (str) => {
    if (!str) return '';
    return str.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  // Check for duplicate relationships before creating
  const checkForDuplicate = (relationshipData) => {
    if (!Array.isArray(relationships) || relationships.length === 0) {
      return null;
    }

    const { contractType, firstName, middleName, lastName, businessName, email, type } = relationshipData;
    const normalizedEmail = email ? normalizeString(email) : '';

    if (contractType === 'Individual') {
      const normalizedFirstName = normalizeString(firstName || '');
      const normalizedMiddleName = normalizeString(middleName || '');
      const normalizedLastName = normalizeString(lastName || '');
      const normalizedFullName = `${normalizedFirstName} ${normalizedMiddleName} ${normalizedLastName}`.trim();

      for (const existing of relationships) {
        if (existing.contractType === 'Individual' && existing.type === type) {
          const existingFirstName = normalizeString(existing.firstName || '');
          const existingMiddleName = normalizeString(existing.middleName || '');
          const existingLastName = normalizeString(existing.lastName || '');
          const existingFullName = `${existingFirstName} ${existingMiddleName} ${existingLastName}`.trim();
          const existingEmail = normalizeString(existing.email || '');

          if (normalizedFullName === existingFullName && normalizedFullName !== '') {
            if (normalizedEmail && existingEmail && normalizedEmail === existingEmail) {
              return {
                existing,
                reason: t('messages:duplicate_relationship_name_email', { 
                  name: `${existing.firstName} ${existing.middleName || ''} ${existing.lastName}`.trim(),
                  email: existing.email
                })
              };
            } else if (!normalizedEmail && !existingEmail) {
              return {
                existing,
                reason: t('messages:duplicate_relationship_name', { 
                  name: `${existing.firstName} ${existing.middleName || ''} ${existing.lastName}`.trim()
                })
              };
            }
          }
        }
      }
    } else if (contractType === 'Company') {
      const normalizedBusinessName = normalizeString(businessName || '');

      for (const existing of relationships) {
        if (existing.contractType === 'Company' && existing.type === type) {
          const existingBusinessName = normalizeString(existing.businessName || '');
          const existingEmail = normalizeString(existing.email || '');

          if (normalizedBusinessName === existingBusinessName && normalizedBusinessName !== '') {
            if (normalizedEmail && existingEmail && normalizedEmail === existingEmail) {
              return {
                existing,
                reason: t('messages:duplicate_relationship_business_email', { 
                  name: existing.businessName,
                  email: existing.email
                })
              };
            } else if (!normalizedEmail && !existingEmail) {
              return {
                existing,
                reason: t('messages:duplicate_relationship_business', { 
                  name: existing.businessName
                })
              };
            }
          }
        }
      }
    }

    return null;
  };

  const handleCreate = () => {
    // Check for duplicates first
    const duplicate = checkForDuplicate(relationship);
    if (duplicate) {
      const existingName = relationship.contractType === 'Individual'
        ? `${duplicate.existing.firstName || ''} ${duplicate.existing.middleName || ''} ${duplicate.existing.lastName || ''}`.trim()
        : duplicate.existing.businessName;
      
      setDuplicateDialog({
        open: true,
        existingRelationship: duplicate.existing,
        message: duplicate.reason || `A relationship with the name "${existingName}" already exists.`
      });
      return;
    }

    const avatar = generateAvatar(relationship);
    console.log('🚀 Creating relationship with data:', {
      name: relationship.name || relationship.businessName,
      type: relationship.type,
      hasAvatar: !!avatar
    });

    dispatch(createRelationship({ ...relationship, avatar }));

    // Check if we came from CreateFilePage
    const fromFilePage = localStorage.getItem('relationshipCreationStartTime') &&
                        sessionStorage.getItem('inPartyCreationFlow') === 'true';

    console.log('🔍 Navigation context check:', {
      fromFilePage,
      hasCreationStartTime: !!localStorage.getItem('relationshipCreationStartTime'),
      inPartyCreationFlow: sessionStorage.getItem('inPartyCreationFlow')
    });

    if (fromFilePage) {
      console.log('🔙 Will return to CreateFilePage after relationship creation');

      // Navigate back after a delay to ensure the relationship is created
      setTimeout(() => {
        console.log('🧭 Navigating back to CreateFilePage...');
        window.history.back();
      }, 1000); // Give time for the relationship to be created and Redux state updated
    } else {
      console.log('📝 Normal relationship creation flow');
      // Normal flow - reset the relationship creation state after a delay
      setTimeout(() => {
        dispatch({ type: "CREATE_RELATIONSHIP_RESET" });
      }, 2000);
    }
  };

  // General Management
  const handleGeneralChange = (field, value) => {
    setRelationship({ ...relationship, [field]: value });
  };

  // Contact Management
  const addContact = () => {
    setRelationship({
      ...relationship,
      contacts: [...relationship.contacts, { name: "", email: "", phone: "" }],
    });
  };

  const updateContact = (index, field, value) => {
    const updatedContacts = relationship.contacts.map((contact, i) => {
      if (i === index) {
        return { ...contact, [field]: value };
      }
      return contact;
    });
    setRelationship({ ...relationship, contacts: updatedContacts });
  };

  const deleteContact = (index) => {
    const updatedContacts = relationship.contacts.filter((_, i) => i !== index);
    setRelationship({ ...relationship, contacts: updatedContacts });
  };

  // Address Management
  const addAddress = () => {
    setRelationship({
      ...relationship,
      addresses: [
        ...relationship.addresses,
        { address: "", postcode: "", place: "" },
      ],
    });
  };

  const updateAddress = (index, field, value) => {
    const updatedAddresses = relationship.addresses.map((address, i) => {
      if (i === index) {
        return { ...address, [field]: value };
      }
      return address;
    });
    setRelationship({ ...relationship, addresses: updatedAddresses });
  };

  const deleteAddress = (index) => {
    const updatedAddresses = relationship.addresses.filter(
      (_, i) => i !== index
    );
    setRelationship({ ...relationship, addresses: updatedAddresses });
  };

  // Bank Account Management
  const addBankAccount = () => {
    setRelationship({
      ...relationship,
      bankAccounts: [
        ...relationship.bankAccounts,
        { accountNumber: "", holder: "", bankAddress: "" },
      ],
    });
  };

  const updateBankAccount = (index, field, value) => {
    const updatedBankAccounts = relationship.bankAccounts.map((account, i) => {
      if (i === index) {
        return { ...account, [field]: value };
      }
      return account;
    });
    setRelationship({ ...relationship, bankAccounts: updatedBankAccounts });
  };

  const deleteBankAccount = (index) => {
    const updatedBankAccounts = relationship.bankAccounts.filter(
      (_, i) => i !== index
    );
    setRelationship({ ...relationship, bankAccounts: updatedBankAccounts });
  };

  // Linked Identity Management
  const addLinkedIdentity = () => {
    setRelationship({
      ...relationship,
      linkedIdentities: [...relationship.linkedIdentities, { identity: "" }],
    });
  };

  const updateLinkedIdentity = (index, value) => {
    const updatedIdentities = relationship.linkedIdentities.map(
      (identity, i) => {
        if (i === index) {
          return { ...identity, identity: value };
        }
        return identity;
      }
    );
    setRelationship({ ...relationship, linkedIdentities: updatedIdentities });
  };

  const deleteLinkedIdentity = (index) => {
    const updatedIdentities = relationship.linkedIdentities.filter(
      (_, i) => i !== index
    );
    setRelationship({ ...relationship, linkedIdentities: updatedIdentities });
  };
  const labelStyles = {
    "& .MuiFormControlLabel-label": {
      fontSize: "11.5px",
      fontFamily:
        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
      fontWeight: 400,
    },
  };
  const sectors = ["Legal", "Corporate", "Finance", "Healthcare", "Technology"]; // Add more sectors as needed

  return userRole === "Assistant Lawyer" ? (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:relationships.create_new')}
        breadcrumbs={getBreadcrumbs(t)}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <Typography variant="h6" component="h1">
          {t('messages:error.unauthorized')}
        </Typography>
      </Box>
    </Container>
  ) : (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:relationships.create_new')}
        breadcrumbs={getBreadcrumbs(t)}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          backgroundColor: "white",
          padding: "1px 24px 24px 24px ",
          margin: "24px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "white",
            marginTop: "24px",
            border: "1px solid #d1d1d1",
          }}
        >
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
            <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                orientation="vertical"
                aria-label="relationship tabs"
                TabIndicatorProps={{
                  sx: {
                    left: 0, // Position the indicator at the start
                  },
                }}
                sx={verticalTabStyles}
              >
                <Tab label={t('common:general')} />
                <Tab label={t('common:contacts')} />
                <Tab label={t('common:addresses')} />
                <Tab label={t('forms:labels.bank_accounts')} />
                {/* <Tab label="Linked Identities" /> */}
              </Tabs>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,

              paddingTop: "48px",
              paddingBottom: "32px",
              paddingLeft: 5,
              width: "calc(100% - 200px)",
            }}
          >
            {tabIndex === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "1rem",
                }}
              >
                {/* Radio Input for Contract Type */}
                <FormControl
                  component="fieldset"
                  required
                  sx={{ marginBottom: 2, width: "100%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: "10.5px",
                      fontFamily:
                        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",

                      fontWeight: 400,
                    }}
                  >
                    {t('forms:labels.contract_type')}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label="contract-type"
                    name="contract-type"
                    value={relationship.contractType}
                    onChange={(e) =>
                      handleGeneralChange("contractType", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="Individual"
                      control={<Radio />}
                      label={t('forms:labels.individual')}
                      sx={labelStyles}
                    />
                    <FormControlLabel
                      sx={labelStyles}
                      value="Company"
                      control={<Radio />}
                      label={t('forms:labels.company')}
                    />
                  </RadioGroup>
                </FormControl>
                {relationship.contractType === "Individual" ? (
                  <>
                    <TextField
                      // variant="standard"
                      label={t('forms:labels.firstName')}
                      value={relationship.firstName}
                      onChange={(e) =>
                        handleGeneralChange("firstName", e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      // variant="standard"
                      label={t('forms:labels.middleName')}
                      value={relationship.middleName}
                      onChange={(e) =>
                        handleGeneralChange("middleName", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      variant="outlined"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      label={t('forms:labels.lastName')}
                      value={relationship.lastName}
                      onChange={(e) =>
                        handleGeneralChange("lastName", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      variant="outlined"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    
                    <TextField
                      label={t('forms:labels.bsn')}
                      value={relationship.bsn}
                      onChange={(e) =>
                        handleGeneralChange("bsn", e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <FormControl
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    >
                      <InputLabel>{t('forms:labels.sex')}</InputLabel>
                      <Select
                        value={relationship.sex}
                        onChange={(e) =>
                          handleGeneralChange("sex", e.target.value)
                        }
                        label={t('forms:labels.sex')}
                      >
                        <MenuItem value="">
                          <em>{t('forms:options.none')}</em>
                        </MenuItem>
                        {sexOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    >
                      <InputLabel>{t('forms:labels.burgerlijke_staat')}</InputLabel>
                      <Select
                        value={relationship.burgerlijkeStaat}
                        onChange={(e) =>
                          handleGeneralChange(
                            "burgerlijkeStaat",
                            e.target.value
                          )
                        }
                        label={t('forms:labels.burgerlijke_staat')}
                      >
                        {maritalStatusOptions.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* <TextField
                      label="Hourly Rate"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      value={relationship.hourlyRate}
                      onChange={(e) =>
                        handleGeneralChange("hourlyRate", e.target.value)
                      }
                      size="small"
                    /> */}
                  </>
                ) : (
                  <>
                    <TextField
                      label={t('forms:labels.business_name')}
                      value={relationship.businessName}
                      onChange={(e) =>
                        handleGeneralChange("businessName", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      label={t('forms:labels.email')}
                      value={relationship.email}
                      onChange={(e) => handleGeneralChange("email", e.target.value)}
                      size="small"
                      variant="outlined"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      label={t('forms:labels.website')}
                      value={relationship.website}
                      onChange={(e) =>
                        handleGeneralChange("website", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      label={t('forms:labels.kvk')}
                      value={relationship.kvk}
                      onChange={(e) =>
                        handleGeneralChange("kvk", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                  </>
                )}
                {/* Other Inputs */}

                {/* Type Field */}
                <FormControl
                  variant="outlined"
                  sx={textFieldStyles({
                    width: "30%",
                    mt: 3,
                    marginBottom: 18,
                  })}
                  size="small"
                  required
                >
                  <InputLabel id="type-select-filled-label" required>{t('common:type')}</InputLabel>
                  <Select
                    labelId="type-select-filled-label"
                    value={relationship.type}
                    onChange={(e) =>
                      handleGeneralChange("type", e.target.value)
                    }
                    size="small"
                    label={t('common:type')}
                    required
                  >
                    <MenuItem value="">
                      <em>{t('forms:options.none')}</em>
                    </MenuItem>
                    <MenuItem value="Client">{t('forms:options.relationship_type.client')}</MenuItem>
                    {userRole === "Manager" && (
                      <MenuItem value="Lawyer">{t('forms:options.relationship_type.lawyer')}</MenuItem>
                    )}
                    <MenuItem value="Counter Party">{t('forms:options.relationship_type.counter_party')}</MenuItem>
                    <MenuItem value="Others">{t('forms:options.relationship_type.others')}</MenuItem>
                  </Select>
                </FormControl>
                {/* Status Field */}
                <FormControl
                  variant="outlined"
                  sx={textFieldStyles({
                    width: "30%",
                    mt: 3,
                    marginBottom: 18,
                  })}
                  required
                  size="small"
                >
                  <InputLabel id="status-select-filled-label" required>
                    {t('common:status')}
                  </InputLabel>
                  <Select
                    labelId="status-select-filled-label"
                    value={relationship.status}
                    onChange={(e) =>
                      handleGeneralChange("status", e.target.value)
                    }
                    size="small"
                    label={t('common:status')}
                    required
                  >
                    <MenuItem value="">
                      <em>{t('forms:options.none')}</em>
                    </MenuItem>
                    <MenuItem value="Active">{t('forms:options.status.active')}</MenuItem>
                    <MenuItem value="Inactive">{t('forms:options.status.inactive')}</MenuItem>
                  </Select>
                </FormControl>
                {relationship.contractType === "Individual" ? (
                  <MUIDatePicker
                    value={relationship.dateOfBirth}
                    onChange={(newValue) => handleGeneralChange("dateOfBirth", newValue)}
                    label={t('forms:labels.date_of_birth')}
                    sx={textFieldStyles({
                      width: "30%",
                      mt: 3,
                      marginBottom: 18,
                    })}
                  />
                ) : (
                  <></>
                )}

                {/* Sector Field */}
                {/* <FormControl variant="standard" sx={{ mr: 3, mt: 3, width: "30%" }}>
                <InputLabel id="sector-select-filled-label">Sector</InputLabel>
                <Select
                  labelId="sector-select-filled-label"
                  value={relationship.sector}
                  onChange={(e) =>
                    handleGeneralChange("sector", e.target.value)
                  }
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                </Select>
              </FormControl> */}
              </Box>
            )}
            {tabIndex === 1 && (
              <Box>
                {relationship.contacts.map((contact, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <TextField
                      label={t('forms:labels.contact_name')}
                      value={contact.name}
                      onChange={(e) =>
                        updateContact(index, "name", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.email')}
                      value={contact.email}
                      onChange={(e) =>
                        updateContact(index, "email", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.phone')}
                      value={contact.phone}
                      onChange={(e) =>
                        updateContact(index, "phone", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <FormControl
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel>{t('forms:labels.position')}</InputLabel>
                      <Select
                        value={contact.position}
                        onChange={(e) =>
                          updateContact(index, "position", e.target.value)
                        }
                        label={t('forms:labels.position')}
                      >
                        <MenuItem value="CEO">{t('forms:options.position.ceo')}</MenuItem>
                        <MenuItem value="CFO">{t('forms:options.position.cfo')}</MenuItem>
                        <MenuItem value="Legal Counsel">{t('forms:options.position.legal_counsel')}</MenuItem>
                        <MenuItem value="HR">{t('forms:options.position.hr')}</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton onClick={() => deleteContact(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={addContact}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('buttons:primary.addContact')}
                </Button>
              </Box>
            )}
            {tabIndex === 2 && (
              <Box>
                {relationship.addresses.map((address, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <TextField
                      label={t('forms:labels.street')}
                      value={address.address}
                      onChange={(e) =>
                        updateAddress(index, "address", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "35%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.postcode')}
                      value={address.postcode}
                      onChange={(e) =>
                        updateAddress(index, "postcode", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "10%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.place')}
                      value={address.place}
                      onChange={(e) =>
                        updateAddress(index, "place", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "15%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <Autocomplete
                      options={europeanCountries}
                      value={europeanCountries.find(country => country.value === (address.country || "Netherlands")) || europeanCountries[0]}
                      onChange={(e, newValue) =>
                        updateAddress(index, "country", newValue?.value || "Netherlands")
                      }
                      getOptionLabel={(option) => option.label || ""}
                      sx={{ width: "20%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('forms:labels.country')}
                          size="small"
                          sx={textFieldStyles({
                            width: "100%",
                            mt: 3,
                          })}
                          variant="outlined"
                        />
                      )}
                    />
                    <IconButton
                      onClick={() => deleteAddress(index)}
                      sx={{ marginLeft: 2 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={addAddress}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('buttons:primary.addAddress')}
                </Button>
              </Box>
            )}
            {tabIndex === 3 && (
              <Box>
                {relationship.bankAccounts.map((account, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <TextField
                      label={t('forms:labels.account_number')}
                      value={account.accountNumber}
                      onChange={(e) =>
                        updateBankAccount(
                          index,
                          "accountNumber",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.account_holder_name')}
                      value={account.holder}
                      onChange={(e) =>
                        updateBankAccount(index, "holder", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.bank_address')}
                      value={account.bankAddress}
                      onChange={(e) =>
                        updateBankAccount(index, "bankAddress", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "40%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <IconButton onClick={() => deleteBankAccount(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {/* <Button
                variant="outlined"
                onClick={addBankAccount}
                style={{ marginBottom: "1rem" }}
              >
                Add Bank Account
              </Button> */}
                <Button
                  variant="text" // Use 'contained', 'outlined', or 'text' as needed
                  color="primary" // Use 'primary', 'secondary', etc. as needed
                  startIcon={<AddIcon />}
                  onClick={addBankAccount}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('buttons:primary.addBank')}
                </Button>
              </Box>
            )}
            {/* {tabIndex === 4 && (
            <Box>
              {relationship.linkedIdentities.map((identity, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <TextField
                    label="Identity"
                    value={identity.identity}
                    onChange={(e) => updateLinkedIdentity(index, e.target.value)}
                    size="small"
                    sx={{ marginRight: 3, width: '30%' }}
                  />
                  <IconButton onClick={() => deleteLinkedIdentity(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button variant="outlined" onClick={addLinkedIdentity} style={{ marginBottom: '1rem' }}>
                Add Linked Identity
              </Button>
            </Box>
          )} */}
          </Box>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            // padding: "10px 16px",
            // background: "#fff",
            zIndex: 10,
            backgroundColor: "white",
            color: "#000000",
            padding: "13px 80px",
          }}
        >
          <CancelButton
            variant="outlined"
            onClick={() => {
              // Check if we came from CreateFilePage
              const fromFilePage = localStorage.getItem('relationshipCreationStartTime')
                                  && sessionStorage.getItem('inPartyCreationFlow') === 'true';

              if (fromFilePage) {
                console.log('Canceling relationship creation - returning to file creation page');
                // Clear the creation flags but keep form data
                sessionStorage.removeItem('inPartyCreationFlow');
                sessionStorage.removeItem('createdRelationship');
                // Navigate back to file creation
                window.history.back();
              } else {
                console.log('Canceling relationship creation - returning to relationships list');
                navigate("/relationships");
              }
            }}
            size="small"
          >
            {t('buttons:secondary.cancel')}
          </CancelButton>
          <SubmitButton onClick={handleCreate} size="small">
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              t('buttons:primary.create')
            )}
          </SubmitButton>
        </Box>
      </Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Duplicate Relationship Warning Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ ...duplicateDialog, open: false })}
        aria-labelledby="duplicate-dialog-title"
        aria-describedby="duplicate-dialog-description"
      >
        <DialogTitle id="duplicate-dialog-title">
          {t('messages:duplicate_relationship_title', { defaultValue: 'Duplicate Relationship Detected' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="duplicate-dialog-description">
            {duplicateDialog.message || t('messages:duplicate_relationship_message', { 
              defaultValue: 'A relationship with the same information already exists in the system.' 
            })}
          </DialogContentText>
          {duplicateDialog.existingRelationship && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('messages:existing_relationship', { defaultValue: 'Existing Relationship:' })}
              </Typography>
              <Typography variant="body2">
                <strong>{t('common:name', { defaultValue: 'Name' })}:</strong>{' '}
                {duplicateDialog.existingRelationship.contractType === 'Individual'
                  ? `${duplicateDialog.existingRelationship.firstName || ''} ${duplicateDialog.existingRelationship.middleName || ''} ${duplicateDialog.existingRelationship.lastName || ''}`.trim()
                  : duplicateDialog.existingRelationship.businessName}
              </Typography>
              {duplicateDialog.existingRelationship.email && (
                <Typography variant="body2">
                  <strong>{t('common:email', { defaultValue: 'Email' })}:</strong>{' '}
                  {duplicateDialog.existingRelationship.email}
                </Typography>
              )}
              {duplicateDialog.existingRelationship.uniqueNumber && (
                <Typography variant="body2">
                  <strong>{t('common:unique_number', { defaultValue: 'Unique Number' })}:</strong>{' '}
                  {duplicateDialog.existingRelationship.uniqueNumber}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDuplicateDialog({ ...duplicateDialog, open: false })}
            color="primary"
          >
            {t('buttons:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={() => {
              setDuplicateDialog({ ...duplicateDialog, open: false });
              navigate(`/relationships/${duplicateDialog.existingRelationship?._id}`);
            }}
            color="primary"
            variant="contained"
          >
            {t('buttons:view_existing', { defaultValue: 'View Existing' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateRelationshipPage;
